import { useState, useCallback, useRef, useEffect } from 'react';
import { apiService } from '../services/api';
import {
  ChatMessage,
  ChatSuggestion,
  ChatSuggestionType,
  TableSummary,
} from '../types/unifiedSchema';

let messageIdCounter = 0;
const nextId = () => `msg-${++messageIdCounter}-${Date.now()}`;

// ── Session-storage helpers for chat persistence ──────────────────────
import { CHAT_STORAGE_PREFIX } from '../utils/storageKeys';
const STORAGE_KEY_PREFIX = CHAT_STORAGE_PREFIX;

/** Serialisable version of ChatMessage (Date → ISO string) */
interface StoredMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  suggestions?: ChatSuggestion[];
}

function persistMessages(batchId: string, messages: ChatMessage[]): void {
  try {
    const serialised: StoredMessage[] = messages.map((m) => ({
      ...m,
      timestamp: m.timestamp instanceof Date ? m.timestamp.toISOString() : String(m.timestamp),
    }));
    sessionStorage.setItem(`${STORAGE_KEY_PREFIX}${batchId}`, JSON.stringify(serialised));
  } catch {
    // quota exceeded — silently drop
  }
}

function loadMessages(batchId: string): ChatMessage[] {
  try {
    const raw = sessionStorage.getItem(`${STORAGE_KEY_PREFIX}${batchId}`);
    if (!raw) return [];
    const stored: StoredMessage[] = JSON.parse(raw);
    return stored.map((m) => ({ ...m, timestamp: new Date(m.timestamp) }));
  } catch {
    return [];
  }
}

/** Describes what the user is currently viewing in the UI */
export interface ViewContext {
  /** Name / slug of the currently-selected table (file_name from summary) */
  selectedTable?: string;
  /** Human-readable semantic name of the selected table */
  selectedTableSemanticName?: string;
  /** Active detail tab: "overview" | "columns" | "keys_joins" | "preview" */
  activeTab?: string;
}

const TAB_LABELS: Record<number, string> = {
  0: 'overview',
  1: 'columns',
  2: 'keys_joins',
  3: 'preview',
};

export function viewContextFromTabIndex(
  table?: { file_name: string; semantic_name?: string },
  tabIndex?: number,
): ViewContext {
  return {
    selectedTable: table?.file_name,
    selectedTableSemanticName: table?.semantic_name,
    activeTab: tabIndex !== undefined ? TAB_LABELS[tabIndex] ?? `tab_${tabIndex}` : undefined,
  };
}

// Build a compact schema context for the LLM (cap at ~100 columns)
function buildContext(
  summaries: TableSummary[],
  viewCtx?: ViewContext,
): Record<string, any> {
  let totalCols = 0;
  const tables = summaries.map((t) => {
    const cols = (t.column_summaries || []).slice(0, 30).map((c) => ({
      name: c.name,
      type: c.data_type || 'unknown',
      role: c.semantic_role,
      pk: c.is_primary_key || false,
      fk: c.is_foreign_key || false,
      fk_ref: c.fk_references || undefined,
    }));
    totalCols += cols.length;
    return {
      file_name: t.file_name,
      semantic_name: t.semantic_name,
      entity_type: t.entity_type,
      primary_keys: t.primary_key_candidates || [],
      joins: (t.likely_joins || []).map((j) => ({
        on: j.on,
        to: j.to,
        type: j.join_type,
      })),
      columns: totalCols <= 100 ? cols : cols.slice(0, 5),
    };
  });

  const ctx: Record<string, any> = { tables };

  // Attach a natural-language description of what the user is currently looking at
  // so the LLM can tailor its response (e.g. answer about the table in focus).
  if (viewCtx?.selectedTable) {
    const tabDescriptions: Record<string, string> = {
      overview: 'the overview (entity type, description, row/column statistics)',
      columns: 'the column details (names, types, semantic roles, null %, unique count, descriptions)',
      keys_joins: 'the primary keys and foreign-key relationships / joins',
      preview: 'a preview of the raw data rows',
    };
    const tabDesc = viewCtx.activeTab
      ? tabDescriptions[viewCtx.activeTab] ?? viewCtx.activeTab
      : 'the table overview';

    ctx.user_is_viewing = {
      table: viewCtx.selectedTable,
      semantic_name: viewCtx.selectedTableSemanticName || viewCtx.selectedTable,
      section: viewCtx.activeTab || 'overview',
      description: `The user is currently looking at table "${viewCtx.selectedTableSemanticName || viewCtx.selectedTable}" — specifically ${tabDesc}. When the user says "this table" or asks a question without specifying a table, they are referring to this table.`,
    };
  }

  return ctx;
}

// Parse action blocks from assistant response
function parseActions(text: string): ChatSuggestion[] {
  const suggestions: ChatSuggestion[] = [];
  const actionBlockRegex = /```json:action\s*\n([\s\S]*?)```/g;
  let match;
  while ((match = actionBlockRegex.exec(text)) !== null) {
    try {
      const parsed = JSON.parse(match[1]);
      const actions = Array.isArray(parsed) ? parsed : [parsed];
      for (const action of actions) {
        suggestions.push({
          id: `sug-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          type: action.type as ChatSuggestionType,
          description: action.description || '',
          payload: action.payload || action,
          status: 'pending',
        });
      }
    } catch {
      // Skip malformed action blocks
    }
  }
  return suggestions;
}

// Strip action blocks from message text for display
function stripActionBlocks(text: string): string {
  return text.replace(/```json:action\s*\n[\s\S]*?```/g, '').trim();
}

export function useSchemaChatState(
  batchId: string,
  summaries: TableSummary[],
  onSchemaModified?: () => void,
) {
  // Initialise from sessionStorage so chat survives page refreshes
  const [messages, setMessages] = useState<ChatMessage[]>(() => loadMessages(batchId));
  const [isTyping, setIsTyping] = useState(false);

  // Persist messages to sessionStorage whenever they change
  useEffect(() => {
    persistMessages(batchId, messages);
  }, [batchId, messages]);

  // Mutable ref so sendMessage always reads the latest viewContext
  // without re-creating the callback
  const viewCtxRef = useRef<ViewContext | undefined>(undefined);

  const setViewContext = useCallback((ctx: ViewContext | undefined) => {
    viewCtxRef.current = ctx;
  }, []);

  const sendMessage = useCallback(
    async (text: string) => {
      const userMsg: ChatMessage = {
        id: nextId(),
        role: 'user',
        content: text,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, userMsg]);
      setIsTyping(true);

      try {
        const context = buildContext(summaries, viewCtxRef.current);
        const response = await apiService.chat(batchId, text, context);
        const rawContent = response.message || 'No response received.';
        const suggestions = parseActions(rawContent);
        const displayContent = stripActionBlocks(rawContent) || rawContent;

        const assistantMsg: ChatMessage = {
          id: nextId(),
          role: 'assistant',
          content: displayContent,
          timestamp: new Date(),
          suggestions: suggestions.length > 0 ? suggestions : undefined,
        };
        setMessages((prev) => [...prev, assistantMsg]);

        // If the agent modified the schema via write tools, trigger a refresh
        if (response.schema_modified && onSchemaModified) {
          onSchemaModified();
        }
      } catch (err: any) {
        const errorMsg: ChatMessage = {
          id: nextId(),
          role: 'assistant',
          content: `Error: ${err?.message || 'Failed to get response'}`,
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, errorMsg]);
      } finally {
        setIsTyping(false);
      }
    },
    [batchId, summaries, onSchemaModified]
  );

  const updateSuggestionStatus = useCallback(
    (messageId: string, suggestionId: string, status: 'accepted' | 'rejected') => {
      setMessages((prev) =>
        prev.map((msg) => {
          if (msg.id !== messageId || !msg.suggestions) return msg;
          return {
            ...msg,
            suggestions: msg.suggestions.map((s) =>
              s.id === suggestionId ? { ...s, status } : s
            ),
          };
        })
      );
    },
    []
  );

  const clearMessages = useCallback(() => {
    setMessages([]);
    sessionStorage.removeItem(`${STORAGE_KEY_PREFIX}${batchId}`);
  }, [batchId]);

  return {
    messages,
    isTyping,
    sendMessage,
    setViewContext,
    updateSuggestionStatus,
    clearMessages,
  };
}
